import { create } from "zustand";
import type {
  DrawingInstance,
  DrawingSettingsUpdate,
  DrawingsList,
  DrawingsStore,
} from "./types";
import {
  getStoredDrawings,
  saveDrawings,
  subscribeToDrawingsStorageChanges,
} from "./storage";

/** Known top-level keys on a persisted DrawingInstance. */
const DRAWING_INSTANCE_KEYS = [
  "id",
  "name",
  "symbolKey",
  "windowId",
  "chartPanelId",
  "addedAt",
  "type",
  "visible",
  "settings",
] as const;

/**
 * DR-05: strip unknown top-level keys (e.g. stray `points` / `angle` that used
 * to leak in from spread-based merges or whole-instance saves) from a persisted
 * instance so polluted localStorage self-heals on load.
 */
function sanitizeInstance(obj: unknown): DrawingInstance | null {
  if (!obj || typeof obj !== "object") return null;
  const source = obj as Record<string, unknown>;
  const clean: Record<string, unknown> = {};
  for (const key of DRAWING_INSTANCE_KEYS) {
    if (key in source) clean[key] = source[key];
  }
  return clean as unknown as DrawingInstance;
}

function mergeSettingsUpdate(
  obj: DrawingInstance,
  newUpdate: DrawingSettingsUpdate
): DrawingInstance {
  // DR-05: only ever merge whitelisted instance-level fields. `points` and
  // `angle` on the update belong inside `settings`, not at the top level, so
  // they must not spread onto the instance.
  const merged: DrawingInstance = { ...obj };
  if (newUpdate.name !== undefined) merged.name = newUpdate.name;
  if (newUpdate.symbolKey !== undefined) merged.symbolKey = newUpdate.symbolKey;
  if (newUpdate.visible !== undefined) merged.visible = newUpdate.visible;

  if (newUpdate.points && !newUpdate.settings) {
    merged.settings = {
      ...obj.settings,
      points: newUpdate.points,
      ...(newUpdate.angle !== undefined ? { angle: newUpdate.angle } : {}),
    };
    return merged;
  }

  merged.settings = {
    ...obj.settings,
    ...newUpdate.settings,
  };
  return merged;
}

export const useDrawingsStore = create<DrawingsStore>((set, get) => ({
  objects: [],

  loadObjects: () => {
    const stored = getStoredDrawings();
    // DR-05: drop unknown top-level keys from persisted instances so any
    // localStorage polluted by earlier spread-based saves self-heals.
    const objects = stored
      .map(sanitizeInstance)
      .filter((obj): obj is DrawingInstance => obj !== null);
    // Persist only if sanitizing actually removed something (dropped an
    // instance or stripped a top-level key), so a clean load never rewrites.
    const changed =
      objects.length !== stored.length ||
      objects.some(
        (obj, i) =>
          Object.keys(obj).length !==
          Object.keys(stored[i] as unknown as Record<string, unknown>).length
      );
    set({ objects });
    if (changed) saveDrawings(objects);
  },

  addDrawing: (objectWithoutId: Omit<DrawingInstance, "addedAt">) => {
    const addedAt = Date.now();
    const newObject: DrawingInstance = { ...objectWithoutId, addedAt };
    const updatedObjects = [...get().objects, newObject];
    set({ objects: updatedObjects });
    saveDrawings(updatedObjects);
  },

  addChartObject: (objectWithoutId) => {
    get().addDrawing(objectWithoutId);
  },

  deleteDrawingById: (id: string) => {
    const updatedObjects = get().objects.filter((obj) => obj.id !== id);
    set({ objects: updatedObjects });
    saveDrawings(updatedObjects);
  },

  deleteChartObjectById: (id) => {
    get().deleteDrawingById(id);
  },

  deleteDrawingsForSymbol: (symbolKey: string) => {
    const updatedObjects = get().objects.filter((obj) => obj.symbolKey !== symbolKey);
    set({ objects: updatedObjects });
    saveDrawings(updatedObjects);
  },

  updateDrawingSettings: (id: string, newUpdate: DrawingSettingsUpdate) => {
    const updatedObjects = get().objects.map((obj) =>
      obj.id === id ? mergeSettingsUpdate(obj, newUpdate) : obj
    );
    set({ objects: updatedObjects });
    saveDrawings(updatedObjects);
  },

  replaceDrawingSettings: (id: string, newUpdate: DrawingSettingsUpdate) => {
    const updatedObjects = get().objects.map((obj) =>
      obj.id === id
        ? {
            ...obj,
            ...newUpdate,
            settings: newUpdate.settings ?? obj.settings,
          }
        : obj
    );
    set({ objects: updatedObjects });
    saveDrawings(updatedObjects);
  },

  updateChartObjectSettings: (id, newUpdate) => {
    get().updateDrawingSettings(id, newUpdate);
  },

  getDrawingsBySymbol: (symbolKey: string): DrawingsList => {
    return get().objects.filter((obj) => obj.symbolKey === symbolKey);
  },

  getObjectsBySymbol: (symbolKey) => get().getDrawingsBySymbol(symbolKey),

  getLastSixDrawings: (): DrawingsList => {
    return get()
      .objects.slice()
      .sort((a, b) => b.addedAt - a.addedAt)
      .slice(0, 6);
  },

  getLastSixObjects: () => get().getLastSixDrawings(),

  clearAllDrawings: () => {
    set({ objects: [] });
    saveDrawings([]);
  },

  pruneOrphanDrawings: (): number => {
    const objects = get().objects;
    const kept = objects.filter(
      (obj) => typeof obj.symbolKey === "string" && obj.symbolKey.length > 0
    );
    if (kept.length === objects.length) return 0;
    set({ objects: kept });
    saveDrawings(kept);
    return objects.length - kept.length;
  },
}));

/** @deprecated alias */
export const useChartObjectsStore = useDrawingsStore;

export function initializeDrawingsStore() {
  useDrawingsStore.getState().loadObjects();
  // DR-06: one-time sweep dropping orphaned instances persisted with an empty
  // symbolKey (e.g. created before a symbol was selected). They can never be
  // matched to a chart, so purge them from storage on startup.
  useDrawingsStore.getState().pruneOrphanDrawings();
  // Cross-window sync (TS-03): reload drawings when another window writes the
  // storage key. loadObjects only reads, so this cannot trigger a save loop.
  subscribeToDrawingsStorageChanges(() => {
    useDrawingsStore.getState().loadObjects();
  });
}

/** @deprecated */
export const initializeChartObjectsStore = initializeDrawingsStore;
